import { Badge, Button, EmptyState, Spinner, Table, type TableColumn } from '@bennows/boows-react';
import { CreditCard, ExternalLink, PlusCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Figure, FigureGroup } from '../../components/Figures';
import { ApiFailure } from '../../lib/api';
import { formatDate, formatDateTime, formatMoney, formatNumber } from '../../lib/format';
import { BILLING_TONES } from './billingTone';
import { CreditAdjustmentDialog } from './CreditAdjustmentDialog';
import { useBillingJournal, useTenantBilling, type Invoice, type JournalEntry } from './useTenantBilling';

const CARD_BREAKPOINT = 640;
/** Journal kinds that take credits away; the platform sends every amount positive. */
const OUTGOING = new Set<JournalEntry['kind']>(['reserve', 'charge', 'expire']);

/**
 * Billing of one tenant, live from the license platform (REQ-140-014,
 * REQ-140-023): mode, plan, subscription, balance, credit journal and
 * invoices; credit adjustments for operators who may (REQ-140-020; billing mode
 * and trial end are a page action, REQ-140-022). A platform failure leaves the rest of the page usable (AC-140-009).
 */
export function TenantBillingTab({ tenantId, tenantName, permissions }: { readonly tenantId: string; readonly tenantName: string; readonly permissions: readonly string[] }) {
  const { t } = useTranslation();
  const billing = useTenantBilling(tenantId);
  const more = useBillingJournal(tenantId, billing.data?.journal?.next_cursor ?? null);
  const [adjusting, setAdjusting] = useState(false);

  if (billing.isPending) {
    return (
      <div className="ui-console-loading" role="status">
        <Spinner label={t('common.loading')} />
      </div>
    );
  }
  if (billing.isError) {
    const down = billing.error instanceof ApiFailure && billing.error.slug === 'platform-unavailable';
    return (
      <div className="ui-error-state" role="alert">
        <p className="ui-error-state__text">{t(down ? 'tenantBilling.platformDown' : 'tenantBilling.loadError')}</p>
        <Button variant="secondary" onClick={() => void billing.refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }
  const data = billing.data;
  if (!data.linked || !data.subscription || !data.balance) {
    return <EmptyState icon={<CreditCard aria-hidden="true" />} title={t('tenantBilling.unlinkedTitle')} hint={t('tenantBilling.unlinkedText')} />;
  }

  const { subscription, balance } = data;
  const mode = data.billing_mode ?? 'none';
  const firstPage = data.journal?.entries ?? [];
  const entries = [...firstPage, ...(more.data?.pages.flatMap((page) => page.entries) ?? [])];
  const hasMore = more.data ? more.hasNextPage : data.journal?.next_cursor != null;
  const canAdjust = permissions.includes('credits:grant');

  const entryText = (entry: JournalEntry) => entry.note ?? (entry.task ? t(`aiJobs.${entry.task}`, { defaultValue: t('tenantBilling.journal.task', { task: entry.task }) }) : '');
  const signed = (entry: JournalEntry) => `${OUTGOING.has(entry.kind) ? '−' : '+'}${formatNumber(entry.amount)}`;
  const journalColumns: TableColumn<JournalEntry>[] = [
    { key: 'when', header: t('tenantBilling.journal.when'), cell: (row) => formatDateTime(row.occurred_at) },
    { key: 'kind', header: t('tenantBilling.journal.kind'), cell: (row) => t(`tenantBilling.kinds.${row.source && row.kind === 'grant' ? row.source : row.kind}`) },
    { key: 'amount', header: t('tenantBilling.journal.amount'), align: 'right', cell: signed },
    { key: 'text', header: t('tenantBilling.journal.text'), cell: (row) => entryText(row) || t('tenantBilling.journal.noText') },
  ];
  const invoiceColumns: TableColumn<Invoice>[] = [
    { key: 'date', header: t('tenantBilling.invoices.date'), cell: (row) => formatDate(row.created_at) },
    { key: 'number', header: t('tenantBilling.invoices.number'), cell: (row) => row.number ?? t('tenantBilling.invoices.draft') },
    { key: 'status', header: t('tenantBilling.invoices.status'), cell: (row) => t(`tenantBilling.invoiceStates.${row.status ?? 'unknown'}`, { defaultValue: row.status ?? '' }) },
    { key: 'total', header: t('tenantBilling.invoices.total'), align: 'right', cell: (row) => formatMoney(row.total) },
    {
      key: 'open',
      header: t('tenantBilling.invoices.open'),
      cell: (row) =>
        row.hosted_invoice_url ? (
          <a href={row.hosted_invoice_url} target="_blank" rel="noreferrer noopener">
            {t('tenantBilling.invoices.view')}
          </a>
        ) : null,
    },
  ];

  return (
    <>
      {canAdjust ? (
        <div className="ui-console-toolbar">
          <Button iconPrefix={<PlusCircle aria-hidden="true" />} onClick={() => setAdjusting(true)}>
            {t('tenantBilling.adjust.open')}
          </Button>
        </div>
      ) : null}
      <FigureGroup
        title={t('tenantBilling.plan.title')}
        action={
          subscription.stripe_customer_url ? (
            <a className="ui-console-link" href={subscription.stripe_customer_url} target="_blank" rel="noreferrer noopener">
              {t('tenantBilling.plan.stripe')}
              <ExternalLink aria-hidden="true" size={14} />
            </a>
          ) : undefined
        }
      >
        <Figure text label={t('tenant.billing.state')} value={<Badge tone={BILLING_TONES[mode]}>{t(`billingStates.${mode}`)}</Badge>} />
        <Figure text label={t('tenantBilling.plan.plan')} value={data.plan ? data.plan.name : t('tenantBilling.plan.noPlan')} />
        <Figure text label={t('tenantBilling.plan.status')} value={t(`tenantBilling.subscriptionStates.${subscription.status}`)} />
        {subscription.trial_ends_at ? <Figure text label={t('tenantBilling.plan.trialEnds')} value={formatDate(subscription.trial_ends_at)} /> : null}
        {subscription.current_period_end ? (
          <Figure
            text
            label={t(subscription.cancel_at_period_end ? 'tenantBilling.plan.endsAt' : 'tenantBilling.plan.renewsAt')}
            value={formatDate(subscription.current_period_end)}
          />
        ) : null}
      </FigureGroup>
      <FigureGroup
        title={t('tenantBilling.balance.title')}
        note={balance.next_expiry ? t('tenantBilling.balance.nextExpiry', { amount: formatNumber(balance.next_expiry.amount), date: formatDate(balance.next_expiry.at) }) : undefined}
      >
        <Figure label={t('tenantBilling.balance.available')} value={formatNumber(balance.available)} />
        <Figure label={t('tenantBilling.balance.reserved')} value={formatNumber(balance.reserved)} />
        <Figure label={t('tenantBilling.balance.granted')} value={formatNumber(balance.granted)} />
        <Figure label={t('tenantBilling.balance.usedThisMonth')} value={formatNumber(balance.used_this_month)} />
      </FigureGroup>
      <section className="ui-card ui-stack" aria-label={t('tenantBilling.journal.title')}>
        <h2 className="ui-console-figures__title">{t('tenantBilling.journal.title')}</h2>
        <Table
          ariaLabel={t('tenantBilling.journal.title')}
          rows={entries}
          columns={journalColumns}
          getRowId={(row) => row.id}
          emptyState={<EmptyState title={t('tenantBilling.journal.empty')} />}
          labels={{ empty: t('tenantBilling.journal.empty') }}
          cardBreakpoint={CARD_BREAKPOINT}
          viewToggle={false}
          mobileViewToggle={false}
          renderCard={(row) => (
            <div className="ui-console-card">
              <strong>
                {signed(row)} · {t(`tenantBilling.kinds.${row.source && row.kind === 'grant' ? row.source : row.kind}`)}
              </strong>
              <span className="ui-console-muted">{formatDateTime(row.occurred_at)}</span>
              {entryText(row) ? <span>{entryText(row)}</span> : null}
            </div>
          )}
        />
        {hasMore ? (
          <div className="ui-console-more">
            <Button variant="secondary" loading={more.isFetching} onClick={() => void (more.data ? more.fetchNextPage() : more.refetch())}>
              {t('tenantBilling.journal.more')}
            </Button>
          </div>
        ) : null}
        {more.isError ? (
          <p className="ui-auth__message" role="alert">
            {t('tenantBilling.journal.moreError')}
          </p>
        ) : null}
      </section>
      <section className="ui-card ui-stack" aria-label={t('tenantBilling.invoices.title')}>
        <h2 className="ui-console-figures__title">{t('tenantBilling.invoices.title')}</h2>
        <Table
          ariaLabel={t('tenantBilling.invoices.title')}
          rows={data.invoices ?? []}
          columns={invoiceColumns}
          getRowId={(row) => row.id}
          emptyState={<EmptyState title={t('tenantBilling.invoices.empty')} />}
          labels={{ empty: t('tenantBilling.invoices.empty') }}
          cardBreakpoint={CARD_BREAKPOINT}
          viewToggle={false}
          mobileViewToggle={false}
          renderCard={(row) => (
            <div className="ui-console-card">
              <strong>
                {formatMoney(row.total)} · {row.number ?? t('tenantBilling.invoices.draft')}
              </strong>
              <span className="ui-console-muted">{formatDate(row.created_at)}</span>
              {row.hosted_invoice_url ? (
                <a href={row.hosted_invoice_url} target="_blank" rel="noreferrer noopener">
                  {t('tenantBilling.invoices.view')}
                </a>
              ) : null}
            </div>
          )}
        />
      </section>
      {adjusting ? <CreditAdjustmentDialog tenantId={tenantId} tenantName={tenantName} available={balance.available} onClose={() => setAdjusting(false)} /> : null}
    </>
  );
}
